import {
  action,
  atom,
  onConnect,
  reaction,
  withAssign,
  type Atom,
  type AtomMut,
} from '@reatom/framework'
import BigNumber from 'bignumber.js'
import * as DateF from 'date-fns'

import { Array, AsyncStateN, Order, pipe, Result } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { reatomRequest } from '@root/shared/lib'

import { PnlCalendarDaily } from '../model'

import type { PnlCalendarEntry } from '../model/pnl-calendar-daily'
import type { DelugeId } from '@root/entities/user-short'
import type { RequestAtom } from '@root/shared/lib'

const WEEK_STARTS_ON = 1 as const

/* eslint-disable @reatom/unit-naming-rule -- unit names are prefixed via `${namePrefix}` */
export const reatomPnlCalendarView = (
  dataAtom: Atom<AsyncStateN.AsyncStateN<unknown, Array<PnlCalendarEntry>>>,
  dateAtom: AtomMut<Date>,
  namePrefix: string,
) => {
  const visibleMonthAtom = atom((ctx) => {
    const date = ctx.spy(dateAtom)
    return DateF.startOfMonth(date)
  }, `${namePrefix}VisibleMonthAtom`)

  const dailyByDayAtom = atom<
    Map<PnlCalendarDaily.DayKey, { pnlNative: BigNumber; pnlUsd: BigNumber }>
  >((ctx) => {
    const dataState = ctx.spy(dataAtom)
    if (!AsyncStateN.isSuccess(dataState)) {
      return new Map<
        PnlCalendarDaily.DayKey,
        { pnlNative: BigNumber; pnlUsd: BigNumber }
      >()
    }
    return PnlCalendarDaily.buildDailyPnlByDayKey(dataState.value)
  }, `${namePrefix}DailyByDayAtom`)

  const earliestMonthAtom = atom((ctx) => {
    const dataState = ctx.spy(dataAtom)
    if (!AsyncStateN.isSuccess(dataState)) {
      return DateF.startOfMonth(new Date())
    }
    return PnlCalendarDaily.earliestMonthStart(dataState.value)
  }, `${namePrefix}EarliestMonthAtom`)

  const canGoPrevAtom = atom((ctx) => {
    const earliestMonth = ctx.spy(earliestMonthAtom)
    const visibleMonth = ctx.spy(visibleMonthAtom)
    return DateF.isBefore(earliestMonth, visibleMonth)
  }, `${namePrefix}CanGoPrevAtom`)

  const canGoNextAtom = atom((ctx) => {
    const visibleMonth = ctx.spy(visibleMonthAtom)
    const currentMonthStart = DateF.startOfMonth(new Date())
    return DateF.isBefore(visibleMonth, currentMonthStart)
  }, `${namePrefix}CanGoNextAtom`)

  const gridDaysAtom = atom((ctx) => {
    const visibleMonth = ctx.spy(visibleMonthAtom)
    const monthEnd = DateF.endOfMonth(visibleMonth)
    const gridStart = DateF.startOfWeek(visibleMonth, {
      weekStartsOn: WEEK_STARTS_ON,
    })
    const gridEnd = DateF.endOfWeek(monthEnd, { weekStartsOn: WEEK_STARTS_ON })
    return DateF.eachDayOfInterval({ start: gridStart, end: gridEnd })
  }, `${namePrefix}GridDaysAtom`)

  return {
    visibleMonth: visibleMonthAtom,
    dailyByDay: dailyByDayAtom,
    canGoPrev: canGoPrevAtom,
    canGoNext: canGoNextAtom,
    gridDays: gridDaysAtom,
  }
}

/* eslint-enable @reatom/unit-naming-rule */
export const fetchUserPnlCalendarDataRequestAction = action(
  async (
    ctx,
    {
      network,
      date,
      delugeId,
    }: {
      network: Network.Network
      date?: Date
      delugeId?: DelugeId
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userPnlCalendarList, [
        {
          network: Network.toDto(network),
          currentDate: `${DateF.format(
            DateF.startOfMonth(date ?? new Date()),
            'yyyy-MM',
          )}-01T00:00:00.000Z`,
          delugeId: delugeId,
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.map((it) =>
        pipe(
          it,
          Array.map((it) => ({
            timestamp: new Date(it.date),
            pnlNative: BigNumber(it.pnlNative),
            pnlUsd: BigNumber(it.pnlUsd),
          })),
          Array.sortWith((it) => it.timestamp, Order.Date),
        ),
      ),
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'fetchUserPnlCalendarDataRequestAction',
)

export const reatomPnlCalendar = (delugeId: DelugeId | undefined) => {
  const dateAtom = atom<Date>(new Date(), 'dateAtom')

  const calendarDataAtom = reatomRequest({
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'fetchUserPnlCalendarDataAction',
    execAction: fetchUserPnlCalendarDataRequestAction,
  })

  const loadReaction = reaction((ctx) => {
    const network = ctx.spy(selectedNetworkAtom)
    const date = ctx.spy(dateAtom)
    calendarDataAtom.execute(ctx, {
      network,
      date,
      delugeId,
    })
  }, 'loadReaction')

  const retryAction = action((ctx) => loadReaction(ctx), 'retryAction')

  onConnect(calendarDataAtom, (ctx) => {
    const sub = loadReaction(ctx)
    return () => sub.unsubscribe()
  })

  /* eslint-disable @reatom/unit-naming-rule */
  const calendarView = reatomPnlCalendarView(
    calendarDataAtom,
    dateAtom,
    'pnlCalendar',
  )

  return (calendarDataAtom as RequestAtom.AsAtom<typeof calendarDataAtom>).pipe(
    withAssign(() => ({
      date: dateAtom,
      retryAction,
      ...calendarView,
    })),
  )
}

export type UserPnlCalendarModel = ReturnType<typeof reatomPnlCalendar>
